import React, { useState } from 'react';
import { Form, Input, Button, message, Select, DatePicker, InputNumber } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '../../services/api';
import { ContractDataRow } from './ContractDataRow';
import { SectionHeader, SubsectionHeader } from './SectionHeader';
import { NotesSection } from './NotesSection';
import { CardContainer } from './CardContainer';
import { GridLayout } from './GridLayout';
import { Typography } from '../design-system/typography';
import { colors, spacing, typography } from '../design-system/tokens';
import { ContractDetails as ContractDetailsType, ContractParty } from './types';

const { TextArea } = Input;
const { Option } = Select;

// Contract icon SVG (matching Figma design)
const ContractIcon: React.FC = () => (
  <svg 
    width="18" 
    height="24" 
    viewBox="0 0 18 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M3 2H15C16.1046 2 17 2.89543 17 4V20C17 21.1046 16.1046 22 15 22H3C1.89543 22 1 21.1046 1 20V4C1 2.89543 1.89543 2 3 2Z" 
      stroke="currentColor" 
      strokeWidth="1.5"
    />
    <path 
      d="M5 7H13M5 11H13M5 15H9" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
  </svg>
);

interface PartyWithAddressProps {
  title: string;
  party: ContractParty;
  className?: string;
}

const PartyWithAddress: React.FC<PartyWithAddressProps> = ({ title, party, className = '' }) => {
  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Label */}
      <Typography 
        variant="ledgerLabel" 
        as="div"
        style={{ 
          color: colors.text.primary, 
          textTransform: 'uppercase',
          marginBottom: spacing.xs
        }}
      >
        {title}
      </Typography>
      
      {/* Company Name */}
      <Typography 
        variant="valueAddress" 
        as="div"
        style={{ 
          color: colors.text.primary,
          marginBottom: spacing.xs
        }}
      >
        {party.name}
      </Typography>
      
      {/* Address section with proper line breaks and gray color */}
      <div
        style={{ 
          fontFamily: typography.fontFamily.redditSans,
          fontSize: '14px',
          fontWeight: 400,
          letterSpacing: '0.28px',
          color: '#94B2B8', // Primary/40 color from Figma
          lineHeight: 'normal'
        }}
      >
        {party.address.street}
        <br />
        {party.address.city}{party.address.state ? `, ${party.address.state}` : ''} {party.address.postalCode}
        {party.address.country && (
          <>
            <br />
            {party.address.country}
          </>
        )}
      </div>
    </div>
  );
};

interface ContractDetailsCardProps {
  contract: ContractDetailsType;
  isEditing: boolean;
  onEditToggle: () => void;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  className?: string;
}

const ContractDetailsCard: React.FC<ContractDetailsCardProps> = ({ 
  contract, 
  isEditing,
  onEditToggle,
  onSave,
  onCancel,
  className = '' 
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Fetch payment terms for dropdowns
  const { data: paymentTermsData } = useQuery({
    queryKey: ['payment-terms'],
    queryFn: async () => {
      return await api.get('/payment-terms', { params: { active: true, limit: 100 } }) as any;
    },
    enabled: isEditing,
  });

  // Fetch shipping basis for dropdowns
  const { data: shippingBasisData } = useQuery({
    queryKey: ['shipping-basis'],
    queryFn: async () => {
      return await api.get('/shipping-basis', { params: { active: true, limit: 100 } }) as any;
    },
    enabled: isEditing,
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      // Transform form values to API format
      const apiData: any = {};
      
      if (values.contractDate) {
        apiData.contractDate = values.contractDate.format('YYYY-MM-DD');
      }
      if (values.buyerRef !== undefined) {
        apiData.buyerRef = values.buyerRef;
      }
      if (values.sellerRef !== undefined) {
        apiData.sellerRef = values.sellerRef;
      }
      // Handle date fields - always send to allow clearing
      // Check if value exists and is a valid dayjs object before formatting
      if (values.revisionDate && values.revisionDate.isValid && values.revisionDate.isValid()) {
        apiData.revisionDate = values.revisionDate.format('YYYY-MM-DD');
      } else {
        apiData.revisionDate = null;
      }
      
      if (values.buyerSigned && values.buyerSigned.isValid && values.buyerSigned.isValid()) {
        apiData.buyerSigned = values.buyerSigned.format('YYYY-MM-DD');
      } else {
        apiData.buyerSigned = null;
      }
      
      if (values.sellerSigned && values.sellerSigned.isValid && values.sellerSigned.isValid()) {
        apiData.sellerSigned = values.sellerSigned.format('YYYY-MM-DD');
      } else {
        apiData.sellerSigned = null;
      }
      
      if (values.sellerTerms !== undefined) {
        apiData.termsSellerCd = values.sellerTerms;
      }
      if (values.buyerTerms !== undefined) {
        apiData.termsBuyerCd = values.buyerTerms;
      }
      if (values.sellerBasis !== undefined) {
        apiData.basisSellerCd = values.sellerBasis;
      }
      if (values.buyerBasis !== undefined) {
        apiData.basisBuyerCd = values.buyerBasis;
      }
      if (values.sellerNotes !== undefined) {
        apiData.sellerNotes = values.sellerNotes;
      }
      if (values.buyerNotes !== undefined) {
        apiData.buyerNotes = values.buyerNotes;
      }
      if (values.commissionRate !== undefined) {
        apiData.commissionRate = values.commissionRate;
      }
      if (values.commissionShare !== undefined) {
        apiData.commissionShare = values.commissionShare;
      }
      if (values.marginShare !== undefined) {
        apiData.marginShare = values.marginShare;
      }

      await onSave(apiData);
      message.success('Contract updated successfully');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fix validation errors');
      } else {
        message.error(error.message || 'Failed to update contract');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Helper function to parse dates
  const parseDate = (dateString: string | undefined) => {
    if (!dateString) return undefined;
    
    // Try "DD MMMM YYYY" format first (e.g., "28 February 2025")
    let parsed = dayjs(dateString, 'DD MMMM YYYY', true);
    if (parsed.isValid()) return parsed;
    
    // Try standard ISO format
    parsed = dayjs(dateString);
    if (parsed.isValid()) return parsed;
    
    return undefined;
  };

  // Initialize form with contract data
  React.useEffect(() => {
    if (isEditing) {
      form.setFieldsValue({
        contractDate: parseDate(contract.generalInfo.contractDate),
        buyerRef: contract.generalInfo.buyerRef,
        sellerRef: contract.generalInfo.sellerRef,
        revisionDate: parseDate(contract.generalInfo.revisionDate),
        buyerSigned: parseDate(contract.generalInfo.buyerSigned),
        sellerSigned: parseDate(contract.generalInfo.sellerSigned),
        sellerTerms: contract.paymentInfo.sellerTermsId,
        buyerTerms: contract.paymentInfo.buyerTermsId,
        sellerBasis: contract.paymentInfo.sellerBasisId,
        buyerBasis: contract.paymentInfo.buyerBasisId,
        commissionRate: contract.paymentInfo.commissionRate,
        commissionShare: contract.paymentInfo.commissionShare,
        marginShare: contract.paymentInfo.marginShare,
        sellerNotes: contract.notes.sellerNotes || '',
        buyerNotes: contract.notes.buyerNotes || '',
      });
    }
  }, [isEditing, contract, form]);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      
      {/* Main card header */}
      <CardContainer variant="secondary">
        {/* Contract Header */}
        <SectionHeader
          title="Contract"
          icon={<div style={{ width: '18px', height: '24px' }}><ContractIcon /></div>}
          showBottomBorder={true}
          actions={
            isEditing ? (
              <>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  onClick={handleSave}
                  loading={saving}
                >
                  Save
                </Button>
                <Button 
                  icon={<CloseOutlined />} 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                icon={<EditOutlined />} 
                onClick={onEditToggle}
              >
                Edit
              </Button>
            )
          }
        />

        {/* General Information Section */}
        <div>
          <SubsectionHeader title="General Information" />
          {isEditing ? (
            <Form form={form} layout="vertical">
              <GridLayout columns="repeat(auto-fit, minmax(300px, 1fr))">
                <Form.Item
                  label="Contract Date"
                  name="contractDate"
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD MMMM YYYY"
                    placeholder="Select contract date"
                  />
                </Form.Item>
                <Form.Item
                  label="Buyer Ref"
                  name="buyerRef"
                >
                  <Input placeholder="Enter buyer reference" />
                </Form.Item>
                <Form.Item
                  label="Seller Ref"
                  name="sellerRef"
                >
                  <Input placeholder="Enter seller reference" />
                </Form.Item>
                <Form.Item
                  label="Revision Date"
                  name="revisionDate"
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD MMMM YYYY"
                    placeholder="Select revision date"
                  />
                </Form.Item>
                <Form.Item
                  label="Buyer Signed"
                  name="buyerSigned"
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD MMMM YYYY"
                    placeholder="Select buyer signed date"
                  />
                </Form.Item>
                <Form.Item
                  label="Seller Signed"
                  name="sellerSigned"
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD MMMM YYYY"
                    placeholder="Select seller signed date"
                  />
                </Form.Item>
              </GridLayout>
            </Form>
          ) : (
            <GridLayout columns="repeat(auto-fit, minmax(300px, 1fr))">
              <ContractDataRow label="Contract Date" value={contract.generalInfo.contractDate} />
              <ContractDataRow label="Buyer Ref" value={contract.generalInfo.buyerRef} />
              <ContractDataRow label="Seller Ref" value={contract.generalInfo.sellerRef} />
              <ContractDataRow label="Revision Date" value={contract.generalInfo.revisionDate} />
              <ContractDataRow label="Buyer Signed" value={contract.generalInfo.buyerSigned || 'Not signed'} />
              <ContractDataRow label="Seller Signed" value={contract.generalInfo.sellerSigned || 'Not signed'} />
            </GridLayout>
          )}
        </div>

        {/* Parties Information Section */}
        <div>
          <SubsectionHeader title="Parties Information" />
          <GridLayout columns="repeat(4, 1fr)">
            <PartyWithAddress title="Supplier" party={contract.parties.supplier} />
            <PartyWithAddress title="Seller" party={contract.parties.seller} />
            <PartyWithAddress title="Buyer" party={contract.parties.buyer} />
            {contract.parties.brand && (
              <ContractDataRow label="Brand" value={contract.parties.brand} />
            )}
          </GridLayout>
        </div>

        {/* Payment Information Section */}
        <div>
          <SubsectionHeader title="Payment Information" />
          {isEditing ? (
            <Form form={form} layout="vertical">
              <GridLayout columns="repeat(6, 1fr)">
                <Form.Item
                  label="Seller Terms"
                  name="sellerTerms"
                >
                  <Select
                    placeholder="Select seller terms"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {paymentTermsData?.payment_terms?.map((term: any) => (
                      <Option key={term.id} value={term.id}>
                        {term.terms}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Buyer Terms"
                  name="buyerTerms"
                >
                  <Select
                    placeholder="Select buyer terms"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {paymentTermsData?.payment_terms?.map((term: any) => (
                      <Option key={term.id} value={term.id}>
                        {term.terms}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Seller Basis"
                  name="sellerBasis"
                >
                  <Select
                    placeholder="Select seller basis"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {shippingBasisData?.shipping_basis?.map((basis: any) => (
                      <Option key={basis.id} value={basis.id}>
                        {basis.basis}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Buyer Basis"
                  name="buyerBasis"
                >
                  <Select
                    placeholder="Select buyer basis"
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const children = option?.children as string | undefined;
                      return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  >
                    {shippingBasisData?.shipping_basis?.map((basis: any) => (
                      <Option key={basis.id} value={basis.id}>
                        {basis.basis}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Commission Rate (%)"
                  name="commissionRate"
                >
                  <InputNumber
                    placeholder="e.g., 2.0"
                    min={0}
                    max={100}
                    step={0.1}
                    precision={3}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label="Commission Share (%)"
                  name="commissionShare"
                >
                  <InputNumber
                    placeholder="e.g., 50.0"
                    min={0}
                    max={100}
                    step={1}
                    precision={2}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label="Margin Share (%)"
                  name="marginShare"
                >
                  <InputNumber
                    placeholder="e.g., 50.0"
                    min={0}
                    max={100}
                    step={1}
                    precision={2}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </GridLayout>
            </Form>
          ) : (
            <GridLayout columns="repeat(auto-fit, minmax(150px, 1fr))">
              <ContractDataRow label="Seller Terms" value={contract.paymentInfo.sellerTerms} />
              <ContractDataRow label="Buyer Terms" value={contract.paymentInfo.buyerTerms} />
              <ContractDataRow label="Seller Basis" value={contract.paymentInfo.sellerBasis} />
              <ContractDataRow label="Buyer Basis" value={contract.paymentInfo.buyerBasis} />
              <ContractDataRow
                label="Commission"
                value={contract.paymentInfo.commissionRate != null
                  ? `${contract.paymentInfo.commissionRate}%`
                  : '—'}
              />
              <ContractDataRow
                label="Comm. Share"
                value={contract.paymentInfo.commissionShare != null
                  ? `${contract.paymentInfo.commissionShare}%`
                  : '—'}
              />
              <ContractDataRow
                label="Margin Share"
                value={contract.paymentInfo.marginShare != null
                  ? `${contract.paymentInfo.marginShare}%`
                  : '—'}
              />
            </GridLayout>
          )}
        </div>

        {/* Notes Section */}
        <div>
          <SubsectionHeader title="Notes" />
          {isEditing ? (
            <Form form={form} layout="vertical">
              <GridLayout columns="1fr">
                <Form.Item
                  label="Seller Notes"
                  name="sellerNotes"
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Enter seller notes"
                  />
                </Form.Item>
                <Form.Item
                  label="Buyer Notes"
                  name="buyerNotes"
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Enter buyer notes"
                  />
                </Form.Item>
              </GridLayout>
            </Form>
          ) : (
            <>
              {(contract.notes.sellerNotes || contract.notes.buyerNotes) ? (
                <NotesSection 
                  notes={[
                    ...(contract.notes.sellerNotes ? [{ label: 'Seller Notes', content: contract.notes.sellerNotes }] : []),
                    ...(contract.notes.buyerNotes ? [{ label: 'Buyer Notes', content: contract.notes.buyerNotes }] : [])
                  ]}
                />
              ) : (
                <Typography 
                  variant="valueAddress" 
                  as="div"
                  style={{ color: colors.text.tertiary, fontStyle: 'italic' }}
                >
                  No notes
                </Typography>
              )}
            </>
          )}
        </div>
      </CardContainer>
    </div>
  );
};

export default ContractDetailsCard;